import { Camera, Loader2, RefreshCw, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { SelfieCaptureScreen } from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';

import { useVerifyStep } from '../../api/useVerifyStep';
import type {
  SessionDetailDto,
  SessionStepApiBindingDto,
  SessionStepDto,
} from '../../schemas/sessionSchemas';
import { extractReason } from '../pan/pan.utils';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import {
  CHECK_FAILED_TOAST,
  RETRY_EXHAUSTED_NOTE,
  tryAnotherMethodNote,
} from '../stepFallbackNotes';
import { useCameraCapture } from '../useCameraCapture';
import { LivenessDevSimulator } from './LivenessDevSimulator';

// Fallback binding for legacy sessions whose snapshot carries no bindings —
// submits without a catalogueEntryId, letting the backend run its own chain.
const DEFAULT_BINDING: SessionStepApiBindingDto = {
  catalogueEntryId: '',
  path: '/api/v1/FaceLiveness',
  operationId: 'Post_Liveness_Passive',
  summary: 'Passive Liveness',
  priority: 1,
};

/**
 * Runs one Liveness step in a live session, mirroring the workflow builder's
 * phone journey. The applicant captures a selfie with the FRONT camera; the
 * provider confirms it's a live person (3D Activation / Flashing / Passive).
 * On a technical failure the runner advances to the next enabled mode
 * (visible priority fallback), same as the PAN/Aadhaar/Face runners.
 */
export function LivenessStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { mutate, isPending } = useVerifyStep(session.id);

  const bindings = useMemo(() => {
    const b = step.apiBindings.slice().sort((x, y) => x.priority - y.priority);
    return b.length > 0 ? b : [DEFAULT_BINDING];
  }, [step.apiBindings]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  // Live selfie via the front camera — capture/retake only, never a file upload
  // (a liveness check MUST be a live capture). Released on the terminal screens.
  const cameraActive = !expired && !session.isExpired && step.status !== 'Failed';
  const {
    videoRef,
    captured: imageDataUrl,
    error: cameraError,
    capture,
    retake,
    quality,
    blinks,
    blinkTimedOut,
    blinksRequired,
    blinkAvailable,
    blinkWarming,
    // Blink-to-capture is ON for Liveness and only for Liveness. Face Match needs one good
    // frame; Liveness is where proving a LIVE person is the actual purpose of the step.
  } = useCameraCapture(cameraActive, { blink: true });

  // Same three-state ring as Face Match — one behaviour across both camera steps, since
  // the applicant is being asked for the same thing in the same circle.
  // Neutral — the designer's gradient — while the model loads. Green would claim the
  // frame had been checked when nothing has looked at it yet, and it would sit beside a
  // disabled Capture button saying the opposite.
  const ringState = imageDataUrl || blinkWarming ? 'neutral' : quality.ok ? 'ok' : 'bad';

  // The hint follows the applicant's actual next action, in order of what blocks them:
  //   1. framing — nothing else can start until the face is in the circle
  //   2. blink progress — visible so a registered blink is obvious, rather than leaving them
  //      blinking harder at a silent screen
  //   3. after a timeout, the manual button, because blinking on command is not something
  //      everyone can do
  const captureHint = imageDataUrl
    ? null
    : blinkWarming
      ? 'Getting ready…'
      : !quality.ok
        ? quality.message
        : blinkTimedOut || !blinkAvailable
          ? 'Tap Capture when you are ready'
          : `Blink slowly — ${blinks} of ${blinksRequired}`;

  const active = bindings[activeIdx] ?? bindings[0] ?? DEFAULT_BINDING;

  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  // Business reject (step Failed) → terminal rejection screen (liveness fail,
  // or a Reject risk-rule / condition). A reject ends the session — no retry.
  if (step.status === 'Failed') {
    return <FailureScreen reason={extractReason(step.resultJson)} />;
  }

  function buildInput(): Record<string, unknown> {
    // Applicant data only — the backend verifier's NormalizeInput injects the
    // requestId. Canonical split: frontend sends data, backend shapes the
    // request. fileBase64 = the captured selfie; raw base64 (no "data:" prefix)
    // — the backend SessionFileOffloader moves it to object storage on persist.
    // (This API takes NO consent block.)
    const base64 = (imageDataUrl ?? '').replace(/^data:[^;]+;base64,/, '');
    return { fileBase64: base64 };
  }

  function handleSubmit() {
    if (!imageDataUrl) return;
    submitInput(buildInput());
  }

  // DEV: one-click scenario — submits a `test_case` (backend
  // FaceLivenessMockResponses returns the canned result) through the SAME flow
  // so rules/fallback run.
  function runTestCase(testCase: string) {
    submitInput({ fileBase64: '', test_case: testCase });
  }

  function submitInput(input: Record<string, unknown>) {
    mutate(
      {
        stepId: step.id,
        input,
        catalogueEntryId: active.catalogueEntryId || undefined,
      },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          // Provisional shared-link mode: fresh step ids won't match — the entry
          // screen navigates to the real run on success. Nothing to do here.
          if (!updated) return;
          const status = updated?.status;
          if (status === 'Passed' || status === 'Review' || status === 'Failed') {
            // Final verdict — the parent advances / shows the failure screen.
            return;
          }
          // status Pending → technical provider failure → visible fallback.
          const nextBinding = bindings[activeIdx + 1];
          if (nextBinding) {
            // Applicant-facing: never the provider's internal name (`summary`).
            // Same wording as the other runners.
            setFallbackNote(tryAnotherMethodNote());
            setActiveIdx(activeIdx + 1);
            retake();
          } else {
            toast.error(CHECK_FAILED_TOAST);
            setFallbackNote(RETRY_EXHAUSTED_NOTE);
            setActiveIdx(0);
            retake();
          }
        },
        onError: (error) => {
          if (isSessionExpiredError(error)) {
            setExpired(true);
            return;
          }
          toast.error('Could not reach the server. Please try again.');
        },
      },
    );
  }

  return (
    // `flex-1` so the runner fills the card: the action buttons below carry `mt-auto`
    // and therefore sit at the BOTTOM on every step, in the same place, with the
    // "Secured by" line beneath them. Without it each screen put its button wherever
    // its content happened to end, so the button jumped as the applicant advanced.
    <div className="flex flex-1 flex-col gap-4">
      {/* DEV-ONLY: one-click liveness scenarios (backend FaceLivenessMockResponses)
          to exercise the step's Risk / Advanced Rules. Stripped from prod. */}
      {import.meta.env.DEV && <LivenessDevSimulator onRun={runTestCase} isPending={isPending} />}

      {fallbackNote && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
          {fallbackNote}
        </div>
      )}

      {/* The same circular capture Face Match uses (the designer's
          `.sc-selfie-capture__*`), rather than the older bespoke Liveness frame —
          both steps ask for exactly the same thing, so they should not look like two
          different products. `selfie` hands over OUR stream: this runner owns the
          camera because it is what grabs and uploads the frame, so the component must
          not open a second one. */}
      <SelfieCaptureScreen
        ringState={ringState}
        hint={captureHint}
        title="Liveness Check"
        selfie={
          imageDataUrl ? (
            // Mirrored to match the live preview it was captured from.
            <img
              src={imageDataUrl}
              alt="Captured selfie"
              className="h-full w-full -scale-x-100 object-cover"
            />
          ) : cameraError ? (
            <span className="flex h-full w-full items-center justify-center px-4 text-center text-[11px] leading-tight text-white">
              {cameraError}
            </span>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full -scale-x-100 object-cover"
            />
          )
        }
      />

      {/* LIVE camera capture / retake only — no file upload (a liveness check
          must be performed live). */}
      <button
        type="button"
        onClick={imageDataUrl ? retake : capture}
        // Blocked while the model is still loading: for those few seconds nothing is
        // checking the frame, and that is exactly the frame that must not be taken.
        disabled={!!cameraError || (!imageDataUrl && (!quality.ok || blinkWarming))}
        title={!imageDataUrl && quality.message ? quality.message : undefined}
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {imageDataUrl ? <RefreshCw size={16} /> : <Camera size={16} />}
        {imageDataUrl ? 'Retake' : 'Capture'}
      </button>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isPending || !imageDataUrl}
        style={{
          backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
          color: 'var(--brand-btn-text, #ffffff)',
          borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
          borderRadius: 'var(--brand-btn-radius, 0.75rem)',
        }}
        className="flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
      >
        {isPending && <Loader2 size={15} className="animate-spin" />}
        Continue
      </button>
    </div>
  );
}

// Shown when a Liveness step is rejected (business Failed) — e.g. the liveness
// score was below the threshold or a Reject risk-rule fired. Terminal.
function FailureScreen({ reason }: { reason?: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#DC2626]">
        <X size={60} strokeWidth={3} color="white" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#DC2626]">
        Liveness check failed
      </h2>
      <p className="text-[length:var(--text-14)] text-[#666666]">
        {reason ?? 'The liveness check could not be completed. This session has been rejected.'}
      </p>
    </div>
  );
}
